import { Container, Divider } from "@material-ui/core";
import { Box, Checkbox, Grid, Paper, Typography } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  ModalError,
  Page,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors } from "utils";
import { debounce } from "lodash";

const CostAllocationDetail = () => {
  const classes = globalStyles();
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [loadingPage, setLoadingPage] = useState(true);
  const [summaryDetail, setSummaryDetail] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const [queryParams, setQueryParams] = useState({
    page: 1,
    per_page: 10,
    search: "",
  });

  const handleChangeQueryParams = (value, name) => {
    setQueryParams(prev => ({
      ...prev,
      ...(name !== "page" && { page: 1 }),
      [name]: value,
    }));
  };

  const getSummaryDetail = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/cost-allocation/${id}`,
        { headers }
      );
      const { data } = res?.data;
      setSummaryDetail(data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/cost-allocation/${id}/settlements`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount =
        meta?.total && queryParams?.per_page
          ? Math.ceil(meta.total / queryParams.per_page)
          : 1;
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    const initPage = async () => {
      try {
        await getSummaryDetail();
      } finally {
        setLoadingPage(false);
      }
    };
    initPage();
  }, []);

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [id, queryParams.page, queryParams.per_page, queryParams.search]);

  const contractStatus = summaryDetail?.status;
  const songs = summaryDetail?.contract_songs?.data || [];

  return (
    <Page className={classes.root} title="Cost Allocation Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Cost Allocation Detail"
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Divider className={classes.divider} />

          {/* Section: Cost Allocation Detail */}
          <SectionLabel
            title="Cost Allocation Detail"
            subTitle="This is Cost Allocation Detail information"
            mt="16px"
          />

          {/* Section: Song Contract Information */}
          <SectionLabel
            title="Song Contract Information"
            subTitle="Contract information that has been made"
            mt="16px"
          />
          <Paper variant="outlined" sx={{ borderRadius: "8px", p: 3, mb: 3 }}>
            <Grid container spacing={2}>
              {/* Row 1: Contract Number | Contract Status */}
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Contract Number :
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.contract_number || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Contract Status :
                </Typography>
                <Box mt={0.5}>
                  <StatusChip
                    label={contractStatus === "open" ? "Open" : "Closed"}
                    type={contractStatus === "open" ? "success" : "danger"}
                  />
                </Box>
              </Grid>

              {/* Row 2: Start Date | Automatic Deducted by Sales Invoice */}
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Start Date :
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.start_date
                    ? formatDate(summaryDetail.start_date)
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Automatic Deducted by Sales Invoice
                </Typography>
                <Box>
                  <Checkbox
                    checked={Boolean(summaryDetail?.auto_deduction)}
                    disabled
                    sx={{
                      p: 0,
                      color: "#000",
                      "&.Mui-checked": { color: "#000" },
                      "&.Mui-disabled": { color: "#000" },
                    }}
                  />
                </Box>
              </Grid>

              {/* Row 3: End Date | Threshold */}
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  End Date :
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.end_date
                    ? formatDate(summaryDetail.end_date)
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Treshold
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.threshold != null
                    ? `${summaryDetail.threshold}%`
                    : "-"}
                </Typography>
              </Grid>

              {/* Row 4: Composer ID | Total Cost Allocation */}
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Composer ID :
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.composer_id || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Total Cost Allocation
                </Typography>
                <Typography variant="body1" fontWeight="bold" display="block">
                  <CurrencyDisplay
                    value={summaryDetail?.cost_allocation_value}
                    textAlign="left"
                    prefix="Rp"
                    decimalScale={0}
                  />
                </Typography>
              </Grid>

              {/* Row 5: Composer/Author | Unpaid Amount */}
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Composer/Author :
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.composer_name || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Unpaid Amount
                </Typography>
                <Typography variant="body1" fontWeight="bold" display="block">
                  <CurrencyDisplay
                    value={summaryDetail?.unpaid_amount}
                    prefix="Rp"
                    textAlign="left"
                    decimalScale={0}
                  />
                </Typography>
              </Grid>

              {/* Row 6: Description */}
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Description :
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summaryDetail?.description || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6} />
            </Grid>
          </Paper>

          {/* Section: Song Information */}
          <SectionLabel
            title="Song Information"
            subTitle="Detail song information"
            mt="16px"
            mb="16px"
          />
          <InnoTableV2
            isLoading={false}
            columns={columnSong}
            items={songs}
            isHidePagination
          />

          {/* Section: Cost Allocation Value */}
          <SectionLabel
            title="Settlement History"
            subTitle="Feature to set Advance Payment"
            mt="24px"
            mb="16px"
          />
          <InnoTableV2
            isLoading={loadingPage}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.per_page}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "per_page")
            }
            isHideLastPagination={() => false}
          />
        </Container>
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/cost-allocation",
  },
  {
    label: "Cost Allocation",
    link: "/admin/cost-allocation",
  },
  {
    label: "Cost Allocation Detail",
    active: true,
  },
];

const columnSong = [
  {
    name: "song_title",
    title: "Song",
    renderText: item => item || "-",
  },
  {
    name: "composer_name",
    title: "Composer/Author Name",
    renderText: item => item || "-",
  },
  {
    name: "publisher_percentage",
    title: "Publisher Share Percentage",
    renderText: item => (item != null ? `${item}%` : "-"),
  },
  {
    name: "ownership_percentage",
    title: "Ownership Share Percentage",
    renderText: item => (item != null ? `${item}%` : "-"),
  },
  {
    name: "iswc_code",
    title: "ISWC Code",
    renderText: item => item || "-",
  },
];

const columnTable = [
  {
    name: "date",
    title: "Date",
    renderText: item => formatDate(item) || "-",
  },
  {
    name: "document_number",
    title: "Document Number",
    renderText: item => item || "-",
  },
  {
    name: "amount",
    title: "Paid Amount",
    renderText: item => (
      <CurrencyDisplay
        value={item}
        prefix="Rp"
        decimalScale={0}
        textAlign="left"
      />
    ),
  },
];

export default CostAllocationDetail;
